import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import metricsCache from '../services/metricsCache';

const MetricsContext = createContext({
  isConnected: false,
  globalStats: null,
  refreshGlobalMetrics: () => {},
  invalidateCache: () => {},
  getMetrics: () => {},
  subscribe: () => {},
  unsubscribe: () => {},
  debug: false,
  enableDebugMode: () => {},
  disableDebugMode: () => {},
  errors: {},
  reportError: () => {},
  clearError: () => {}
});

export const useMetricsContext = () => {
  const context = useContext(MetricsContext);
  if (!context) {
    throw new Error('useMetricsContext must be used within a MetricsProvider');
  }
  return context;
};

const MetricsProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(true);
  const [globalStats, setGlobalStats] = useState(null);
  const [debug, setDebug] = useState(false);
  const [metricsErrors, setMetricsErrors] = useState({});

  useEffect(() => {
    // Set up global event listeners
    const handleCacheEvent = (eventType, data) => {
      console.log(`[MetricsProvider] Cache event: ${eventType}`, data);
    };

    const handleError = (eventType, data) => {
      console.error(`[MetricsProvider] Cache error: ${eventType}`, data);
      setIsConnected(false);
      
      // Attempt to reconnect after 5 seconds
      setTimeout(() => {
        setIsConnected(true);
      }, 5000);
    };

    // Listen to cache events
    metricsCache.on('cache_hit', (data) => handleCacheEvent('cache_hit', data));
    metricsCache.on('cache_invalidated', (data) => handleCacheEvent('cache_invalidated', data));
    metricsCache.on('calculation_complete', (data) => handleCacheEvent('calculation_complete', data));
    metricsCache.on('calculation_error', (data) => handleError('calculation_error', data));
    metricsCache.on('subscription_error', (data) => handleError('subscription_error', data));

    // Update global stats periodically
    const updateGlobalStats = () => {
      try {
        const stats = metricsCache.getStats();
        setGlobalStats(stats);
      } catch (error) {
        console.error('[MetricsProvider] Error updating global stats:', error);
      }
    };

    updateGlobalStats();
    const statsInterval = setInterval(updateGlobalStats, 10000); // Update every 10 seconds

    return () => {
      clearInterval(statsInterval);
      metricsCache.removeAllListeners();
    };
  }, []);

  const reportError = useCallback((metricType, error) => {
    setMetricsErrors(prev => ({
      ...prev,
      [metricType]: {
        error: error.message || String(error),
        timestamp: Date.now()
      }
    }));
  }, []);

  const refreshGlobalMetrics = useCallback(async (metricTypes = null) => {
    try {
      if (metricTypes) {
        metricTypes.forEach(type => metricsCache.invalidate(type));
      } else {
        metricsCache.clearAll();
      }
      if (debug) console.log('[MetricsProvider] Global metrics refreshed');
    } catch (error) {
      console.error('[MetricsProvider] Error refreshing global metrics:', error);
      reportError('global_refresh', error);
    }
  }, [debug, reportError]);

  const invalidateCache = useCallback((metricTypes, pattern) => {
    try {
      if (metricTypes) {
        metricTypes.forEach(type => metricsCache.invalidate(type));
      } else {
        metricsCache.clearAll();
      }
      if (debug) console.log('[MetricsProvider] Cache invalidated:', { metricTypes, pattern });
    } catch (error) {
      console.error('[MetricsProvider] Error invalidating cache:', error);
      reportError('invalidate_cache', error);
    }
  }, [debug, reportError]);

  const getMetrics = useCallback(async (metricType, params = {}, forceRefresh = false) => {
    try {
      return await metricsCache.getMetrics(metricType, params, forceRefresh);
    } catch (error) {
      console.error('[MetricsProvider] Error getting metrics:', error);
      reportError(metricType, error);
      throw error;
    }
  }, [reportError]);

  const subscribe = useCallback((metricType, params = {}, callback, updateInterval = null) => {
    try {
      return metricsCache.subscribe(metricType, params, callback, updateInterval);
    } catch (error) {
      console.error('[MetricsProvider] Error subscribing to metrics:', error);
      reportError(`${metricType}_subscription`, error);
      throw error;
    }
  }, [reportError]);

  const unsubscribe = useCallback((subscriptionKey) => {
    try {
      metricsCache.unsubscribe(subscriptionKey);
    } catch (error) {
      console.error('[MetricsProvider] Error unsubscribing from metrics:', error);
    }
  }, []);

  const enableDebugMode = useCallback(() => {
    setDebug(true);
    console.log('🔍 Metrics debug mode enabled');
    console.log('Available at window.metricsDebug:', {
      cache: 'Access to metricsCache instance',
      refresh: 'Refresh all metrics',
      invalidate: 'Invalidate specific metrics',
      stats: 'Get cache statistics'
    });
  }, []);

  const disableDebugMode = useCallback(() => {
    setDebug(false);
    console.log('🔍 Metrics debug mode disabled');
  }, []);

  const clearError = useCallback((metricType) => {
    setMetricsErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[metricType];
      return newErrors;
    });
  }, []);

  // Set up debug tools in development mode
  useEffect(() => {
    if (debug) {
      window.metricsDebug = {
        cache: metricsCache,
        refresh: refreshGlobalMetrics,
        invalidate: invalidateCache,
        stats: () => globalStats,
        errors: metricsErrors,
        clearError,
        enableDebug: enableDebugMode,
        disableDebug: disableDebugMode
      };
    } else {
      delete window.metricsDebug;
    }

    return () => {
      delete window.metricsDebug;
    };
  }, [debug, refreshGlobalMetrics, invalidateCache, globalStats, metricsErrors, clearError, enableDebugMode, disableDebugMode]);

  const contextValue = {
    isConnected,
    globalStats,
    refreshGlobalMetrics,
    invalidateCache,
    getMetrics,
    subscribe,
    unsubscribe,
    debug,
    enableDebugMode,
    disableDebugMode,
    errors: metricsErrors,
    reportError,
    clearError
  };

  return (
    <MetricsContext.Provider value={contextValue}>
      {children}
    </MetricsContext.Provider>
  );
};

export default MetricsProvider;